import axios from 'axios';
import { onBeforeUnmount, ref } from 'vue';

/**
 * One optional image in an editor (banner, link), uploaded the way the logo is
 * (HubLogoCard): a media item on the public disk, whose URL is written to `url`
 * — a ref, usually `toRef(form, 'image_url')`.
 *
 * The upload this editor made and has not saved is deleted when it is
 * replaced, removed, or the editor goes away; `keep()` after a save makes it
 * the item's. The image a saved item already has is live, so it is never
 * deleted from here. Each upload, and each remove, supersedes whatever upload
 * is still going: only the latest may set the image or end the busy state.
 *
 * A save that sends the upload holds it: until the save says how it ended,
 * nothing here deletes it. `release()` on every way out of the save; after a
 * save that kept it, call `keep()` first. If the editor went away meanwhile,
 * the release is when an upload nobody kept is deleted.
 */
export default function useStagedImage(type, url, { initialId = null } = {}) {
  const id = ref(initialId);
  const uploading = ref(false);
  const error = ref('');

  let stagedId = null;
  let tornDown = false;
  let holding = false;
  let seq = 0;

  const deleteMedia = async (mediaId) => {
    if (!mediaId) return;
    try {
      await axios.post(`${window.store.event_backend_api}/media-items/delete`, { id: mediaId });
    } catch {
      // Best effort: an orphan is untidy, not a failure the editor needs to hear about.
    }
  };

  const discardStaged = () => {
    if (holding) return;
    void deleteMedia(stagedId);
    stagedId = null;
  };

  const upload = async (file) => {
    error.value = '';
    uploading.value = true;
    const mine = ++seq;
    try {
      const body = new FormData();
      body.append('driver', 'public');
      body.append('type', type);
      body.append('file', file);
      const { data } = await axios.post(`${window.store.event_backend_api}/media-items`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (tornDown || mine !== seq) {
        void deleteMedia(data?.data?.id);
        return;
      }
      if (data?.data?.guid) {
        discardStaged();
        stagedId = data.data.id ?? null;
        id.value = stagedId;
        url.value = data.data.guid;
      }
    } catch (e) {
      // The server's own reason when it refused the file (type, size).
      if (mine === seq) error.value = e?.response?.data?.message?.file?.[0] ?? 'That image could not be uploaded.';
    } finally {
      if (mine === seq) uploading.value = false;
    }
  };

  const remove = () => {
    seq++;
    uploading.value = false;
    error.value = '';
    discardStaged();
    id.value = null;
    url.value = '';
  };

  const keep = () => {
    stagedId = null;
  };

  const hold = () => {
    holding = true;
  };

  const release = () => {
    holding = false;
    if (tornDown) discardStaged();
  };

  onBeforeUnmount(() => {
    tornDown = true;
    discardStaged();
  });

  return { id, uploading, error, upload, remove, keep, hold, release };
}
